import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { Amplify, ResourcesConfig } from 'aws-amplify';
import amplifyconfig from '../amplifyconfiguration.json';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { sessionStorage } from 'aws-amplify/utils';
import { AmplifyAuthenticatorModule } from '@aws-amplify/ui-angular';
import { PrimeNG } from 'primeng/config';
import { palette } from '@primeng/themes';
import { updatePreset } from '@primeng/themes';
import { GlobalSetupMonitorComponent } from './components/global-setup-monitor-component/global-setup-monitor.component';
import { TabRecoveryService } from './services/tab-recovery.service';
import { PostHogService } from './services/posthog.service';
import { CompaniesService } from './services/companies.service';
import { AuthService } from './services/auth.service';
import { SubscriptionService } from './services/subscription.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone:true,
  imports: [
    CommonModule, 
    RouterOutlet, 
    InputTextModule,
    ButtonModule,
    AmplifyAuthenticatorModule,
    GlobalSetupMonitorComponent
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'ProSales Command';
  private analyticsSubscriptions = new Subscription();

  constructor(
    private primeng: PrimeNG,
    private tabRecoveryService: TabRecoveryService,
    private router: Router,
    private posthog: PostHogService,
    private companiesService: CompaniesService,
    private authService: AuthService,
    private subscriptionService: SubscriptionService
  ) {

    const authConfig: ResourcesConfig['Auth'] = {
      Cognito: {
        userPoolId: amplifyconfig.aws_user_pools_id,
        userPoolClientId: amplifyconfig.aws_user_pools_web_client_id
      }
    };
    cognitoUserPoolsTokenProvider.setAuthConfig(authConfig);
    cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage);

    Amplify.configure({ Auth: authConfig }, { Auth: { tokenProvider: cognitoUserPoolsTokenProvider } });

    // Initialize tab recovery service automatically
    console.log('Tab recovery service initialized');
  }
  
  ngOnInit(): void {
    updatePreset({
      semantic: {
        primary: {
          0: '#ffffff',
          50: '{zinc.50}',
          100: '{zinc.100}',
          200: '{zinc.200}',
          300: '{zinc.300}',
          400: '{zinc.400}',
          500: '{zinc.500}',
          600: '{zinc.600}',
          700: '{zinc.700}',
          800: '{zinc.800}',
          900: '{zinc.900}',
          950: '{zinc.950}'
        }
      }
    })
    this.primeng.ripple.set(true);

    this.initAnalytics();
  }

  ngOnDestroy(): void {
    this.analyticsSubscriptions.unsubscribe();
  }

  /**
   * Wire the two analytics signals that only the root component can see:
   * SPA pageviews, and the user/company identity that every later event inherits.
   *
   * Both are best-effort. Analytics must never be able to break navigation, so
   * everything here is inside try/catch and the PostHog service itself no-ops
   * when no key is configured.
   */
  private initAnalytics(): void {
    // SPA pageviews — posthog's automatic capture only fires on full document
    // loads, which in an Angular app means once per session.
    this.analyticsSubscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(event => {
          try {
            this.posthog.capturePageview(event.urlAfterRedirects);
          } catch {
            // ignore
          }
        })
    );

    // Identity. selectedCompany$ is the one place that reliably carries company
    // id, name and the user's role for that company.
    this.analyticsSubscriptions.add(
      this.companiesService.selectedCompany$.subscribe(company => {
        if (!company) return;
        try {
          const userId = this.authService.userId();
          if (!userId) return;

          this.posthog.identify({
            userId,
            companyId: company.companyId,
            companyName: company.companyName,
            role: company.userRole
          });
        } catch {
          // ignore
        }
      })
    );

    // Tier lives on the subscription, not the company record. Subscribing to the
    // stream once covers all six places the subscription is refreshed.
    this.analyticsSubscriptions.add(
      this.subscriptionService.subscription$.subscribe(subscription => {
        if (!subscription?.tierId) return;
        try {
          this.posthog.setCompanyContext({ tier: subscription.tierId });
        } catch {
          // ignore
        }
      })
    );
  }
}
